import { useCallback, useEffect, useState } from "react";
import { Clock, Globe, Loader2, Mail, MapPin, Pencil, Phone, Plus, Printer, Search, Trash2, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import YardFormDialog from "@/components/YardFormDialog";
import { errMsg, useApi } from "@/lib/api";
import { dt, eur } from "@/lib/format";
import { useMe } from "@/context/MeContext";

const Row = ({ icon: Icon, children }) => (children ? <div className="flex items-start gap-2 text-sm text-slate-700"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" /><span className="whitespace-pre-line break-words">{children}</span></div> : null);

const YardCard = ({ y, grades, isAdmin, onEdit, onDelete }) => {
  const priced = grades.filter((g) => y.prices?.[g.key]);
  return (
    <div className="panel flex flex-col gap-4 p-5 fade-up" data-testid="card-wertstoffhof-item">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold leading-tight">{y.name}</h3>
          <p className="text-sm text-slate-500">{[y.street, [y.zip, y.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</p>
        </div>
        <div className="flex shrink-0">
          <Button variant="ghost" size="icon" onClick={onEdit} data-testid={`btn-edit-yard-${y.id}`}><Pencil className="h-4 w-4" /></Button>
          {isAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="ghost" size="icon" data-testid={`btn-delete-yard-${y.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button></AlertDialogTrigger>
              <AlertDialogContent className="border-slate-200 bg-white">
                <AlertDialogHeader><AlertDialogTitle>„{y.name}“ löschen?</AlertDialogTitle></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Abbrechen</AlertDialogCancel><AlertDialogAction onClick={onDelete} data-testid="btn-confirm-delete-yard">Löschen</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-2">
          <Row icon={Phone}>{y.phone && <a href={`tel:${y.phone}`} className="hover:text-orange-700">{y.phone}</a>}</Row>
          <Row icon={Printer}>{y.fax && `Fax ${y.fax}`}</Row>
          <Row icon={Mail}>{y.email && <a href={`mailto:${y.email}`} className="hover:text-orange-700">{y.email}</a>}</Row>
          <Row icon={Globe}>{y.website && <a href={y.website.startsWith("http") ? y.website : `https://${y.website}`} target="_blank" rel="noreferrer" className="hover:text-orange-700">{y.website}</a>}</Row>
          <Row icon={User}>{y.contact_person}</Row>
          <Row icon={MapPin}>{(y.street || y.city) && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${y.street} ${y.zip} ${y.city}`)}`} target="_blank" rel="noreferrer" className="hover:text-orange-700" data-testid={`link-map-${y.id}`}>Route planen</a>}</Row>
        </div>
        <Row icon={Clock}>{y.opening_hours}</Row>
      </div>
      {y.notes && <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">{y.notes}</p>}
      <div className="border-t border-slate-200 pt-3">
        <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-wider text-slate-500"><span>Ankaufspreise</span>{y.prices_updated_at && <span className="normal-case tracking-normal">Stand {dt(y.prices_updated_at)}</span>}</div>
        {priced.length === 0 ? <p className="text-sm text-slate-500">Noch keine Preise hinterlegt.</p> : (
          <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
            {priced.map((g) => <div key={g.key} className="flex justify-between text-sm"><span className="text-slate-500">{g.name}</span><span className="num text-orange-700">{eur(y.prices[g.key])}</span></div>)}
          </div>
        )}
      </div>
    </div>
  );
};

const Compare = ({ yards, grades }) => (
  <div className="panel overflow-x-auto p-5" data-testid="table-yard-compare">
    <table className="w-full min-w-[640px] text-sm">
      <thead className="text-left text-xs uppercase tracking-wider text-slate-500">
        <tr><th className="pb-3">Sorte</th><th className="pb-3 text-right">Richtpreis</th>{yards.map((y) => <th key={y.id} className="pb-3 text-right">{y.name}</th>)}</tr>
      </thead>
      <tbody>
        {grades.map((g) => {
          const best = Math.max(0, ...yards.map((y) => y.prices?.[g.key] || 0));
          return (
            <tr key={g.key} className="border-t border-slate-200">
              <td className="py-2.5 font-medium">{g.name}</td>
              <td className="num py-2.5 text-right text-slate-500">{eur(g.price_eur_kg)}</td>
              {yards.map((y) => {
                const p = y.prices?.[g.key];
                return <td key={y.id} className={`num py-2.5 text-right ${p && p === best ? "font-semibold text-emerald-600" : "text-slate-700"}`}>{p ? eur(p) : "–"}</td>;
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default function Wertstoffhoefe() {
  const api = useApi();
  const me = useMe();
  const [yards, setYards] = useState(null);
  const [grades, setGrades] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    const [y, g] = await Promise.all([api.get("/yards"), api.get("/grades")]);
    setYards(y.data); setGrades(g.data);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const remove = async (y) => { try { await api.delete(`/yards/${y.id}`); toast.success("Gelöscht"); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (!yards) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>;
  const s = q.toLowerCase();
  const shown = yards.filter((y) => [y.name, y.city, y.zip, y.street].join(" ").toLowerCase().includes(s));

  return (
    <div className="space-y-6" data-testid="yards-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Wertstoffhöfe</h1>
          <p className="mt-1 text-sm text-slate-500">Adressen, Öffnungszeiten, Kontakte und Ankaufspreise je Sorte.</p>
        </div>
        <Button onClick={() => setForm({})} data-testid="btn-create-yard"><Plus className="mr-2 h-4 w-4" /> Wertstoffhof hinzufügen</Button>
      </div>
      <Tabs defaultValue="list">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList><TabsTrigger value="list" data-testid="tab-yard-list">Übersicht</TabsTrigger><TabsTrigger value="compare" data-testid="btn-compare-yard-rates">Preisvergleich</TabsTrigger></TabsList>
          <div className="relative w-full sm:w-72"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, Ort oder PLZ" className="pl-9" data-testid="input-yard-search" /></div>
        </div>
        <TabsContent value="list" className="mt-6">
          {shown.length === 0 ? <p className="py-10 text-center text-sm text-slate-500" data-testid="yards-empty">Keine Wertstoffhöfe gefunden.</p> : (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              {shown.map((y) => <YardCard key={y.id} y={y} grades={grades} isAdmin={me.role === "admin"} onEdit={() => setForm(y)} onDelete={() => remove(y)} />)}
            </div>
          )}
        </TabsContent>
        <TabsContent value="compare" className="mt-6"><Compare yards={shown} grades={grades} /></TabsContent>
      </Tabs>
      {form && <YardFormDialog yard={form.id ? form : null} grades={grades} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}
